// 層級名稱（SPEC #org-tree-model「層級名稱」「內建名稱」）：API 一律回原值，VMS 依使用者語系翻內建鍵。
//   - `@` 開頭 = 內建鍵（@site / @building / @floor / @zone）；tree_settings.levels = null 用內建清單
//   - 自訂名稱照原文顯示；清單刪掉的名稱已在用的節點保留、設定頁標「已不在清單」
//   - 建子節點的預設：上一個兄弟節點的名稱，沒有就清單中父節點名稱的下一個
import { BUILTIN_LEVELS, type TreeSettings } from './orgTreeApi';

type T = (k: string, o?: Record<string, unknown>) => string;

export function isBuiltin(label: string | null | undefined): boolean {
  return !!label && label.startsWith('@');
}

/** 一個層級名稱的顯示文字（內建鍵依語系；舊資料沒有 @ 的 site / building / floor / zone 也當內建）。 */
export function levelText(t: T, label: string | null | undefined): string {
  if (!label) return '';
  if (isBuiltin(label)) return t(`ot.lvl_${label.slice(1)}`, { defaultValue: label.slice(1) });
  if (['site', 'building', 'floor', 'zone'].includes(label)) return t(`ot.lvl_${label}`);
  return label;
}

/** 組織目前的層級清單（null = 內建）。 */
export function levelList(s: TreeSettings | null | undefined): string[] {
  return s?.levels ?? [...BUILTIN_LEVELS];
}

/** 家戶的名稱（leaf_label；沒設 = 地點，個人組織 = 家庭；SPEC #terms-home-ui-name）。句子裡的 {{leaf}} 由 lib/leafTerm.ts 帶。 */
export function leafText(t: T, s: TreeSettings | null | undefined, personal = false): string {
  return s?.leaf_label || t(personal ? 'ot.leaf_personal' : 'ot.leaf_default');
}

/** 這個名稱還在清單裡嗎（不在 = 設定頁標「已不在清單」）。 */
export function inList(s: TreeSettings | null | undefined, label: string | null | undefined): boolean {
  if (!label) return true;
  return levelList(s).includes(label);
}

/** 建子節點時預設的層級名稱。 */
export function defaultLevel(s: TreeSettings | null | undefined, parentLabel: string | null | undefined, lastSiblingLabel?: string | null): string {
  const list = levelList(s);
  if (lastSiblingLabel && list.includes(lastSiblingLabel)) return lastSiblingLabel;
  if (!parentLabel) return list[0] ?? '';
  const i = list.indexOf(parentLabel);
  if (i >= 0 && i + 1 < list.length) return list[i + 1];
  return list[Math.min(Math.max(i, 0), list.length - 1)] ?? '';
}

/**
 * 存自訂清單：驗證並組 from_builtin（只在從內建改成自訂時帶）。
 * rows = 設定頁每列 {text, from?}（from = 原本的內建鍵）；回 null 表示驗證不過（reason）。
 */
export function buildLevelsPut(
  current: TreeSettings | null | undefined,
  rows: { text: string; from?: string | null }[],
  leaf: string,
): { ok: true; body: { levels: string[]; leaf_label: string | null; from_builtin?: Record<string, string> } } | { ok: false; reason: 'too_many' | 'length' | 'at_prefix' | 'duplicate' } {
  const levels = rows.map((r) => r.text.trim()).filter(Boolean);
  if (levels.length > 12) return { ok: false, reason: 'too_many' };
  if (levels.some((x) => x.length > 20)) return { ok: false, reason: 'length' };
  if (levels.some((x) => x.startsWith('@'))) return { ok: false, reason: 'at_prefix' };
  if (new Set(levels).size !== levels.length) return { ok: false, reason: 'duplicate' };
  const leafLabel = leaf.trim() ? leaf.trim() : null;
  if (leafLabel && leafLabel.length > 20) return { ok: false, reason: 'length' };
  const body: { levels: string[]; leaf_label: string | null; from_builtin?: Record<string, string> } = { levels, leaf_label: leafLabel };
  if (current?.levels == null) {
    const fb: Record<string, string> = {};
    for (const r of rows) if (r.from && isBuiltin(r.from) && r.text.trim()) fb[r.from] = r.text.trim();
    if (Object.keys(fb).length) body.from_builtin = fb;
  }
  return { ok: true, body };
}
